import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetAdminAlertsQuery } from "store/api/adminApi"
import { ApiTypes, UITypes } from "types"

import { convertAdminAlertsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsAcknowledgeModal } from "modals/AlertsAcknowledgeModal"
import { AlertsFreezeModal } from "modals/AlertsFreezeModal"
import { AlertsUnfreezeModal } from "modals/AlertsUnfreezeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"
import { WidgetVendorAlerts } from "components/widgets/WidgetVendorAlerts"
import { WidgetVendorAlertStatuses } from "components/widgets/WidgetVendorAlertStatuses"

import { vendorAlertsTableHeaderCells } from "./vendorAlertsTableHeaderCells"

import styles from "./index.module.scss"

// TABLE HEADER

export const Alerts = () => {
  const [triggerGetAdminAlerts, { isFetching, data }] = useLazyGetAdminAlertsQuery()
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const { urlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false)
  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false)
  const isHeaderChecked = useMemo(() => {
    const alertsList = data?.data || []
    if (!alertsList.length) return false
    return alertsList.every(alert => selectedAlerts.some(s => s.id === alert.id))
  }, [data?.data, selectedAlerts])

  const onSelectAlert = (id: number) => {
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      if (data?.data) {
        setSelectedAlerts([...selectedAlerts, data.data.find(item => item.id === id)!])
      }
    }
  }

  const toggleAllAlerts = () => {
    const alertsList = data?.data || []
    if (isHeaderChecked) {
      const currentPageAlertIds = alertsList.map(a => a.id)
      setSelectedAlerts(prev => prev.filter(a => !currentPageAlertIds.includes(a.id)))
    } else {
      setSelectedAlerts(prev => {
        const existingIds = new Set(prev.map(a => a.id))
        const toAdd = alertsList.filter(a => !existingIds.has(a.id))
        return [...prev, ...toAdd]
      })
    }
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAdminAlerts(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const checkSelectedAlertsDiffer = () => {
    if (selectedAlerts.length === 0) return false
    const firstStatus = selectedAlerts[0].status
    const statusDiffer = selectedAlerts.some(alert => alert.status !== firstStatus)

    if (statusDiffer) {
      toast.error("Action cannot be applied. Please select alerts with the same status.")
      return true
    }

    return false
  }

  const onAcknowledgeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowAcknowledgeModal(true)
  }

  const onFreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowFreezeModal(true)
  }

  const onUnfreezeClick = () => {
    if (checkSelectedAlertsDiffer()) return
    setShowUnfreezeModal(true)
  }

  const handleSuccess = () => {
    setShowAcknowledgeModal(false)
    setShowFreezeModal(false)
    setShowUnfreezeModal(false)
    setSelectedAlerts([])
  }

  const getAlerts = () => {
    sendRequest(urlQueryParams)
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    getAlerts()
  }, [urlQueryParams, isFirstRender])

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetVendorAlerts />
        <WidgetVendorAlertStatuses />
      </div>
      <ContentWrapper title="Alerts History">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.tableNavButtonsWrapper}>
            <span
              className={classNames(styles.statusButton, styles.acknowledge, {
                [styles.disabledStatusButton]: selectedAlerts.length === 0,
              })}
              onClick={onAcknowledgeClick}
            >
              Acknowledge
            </span>
            <span
              className={classNames(styles.statusButton, styles.active, {
                [styles.disabledStatusButton]: selectedAlerts.length === 0,
                [styles.frozen]: selectedAlerts.some(item => item.status === "frozen"),
                [styles.active]: selectedAlerts.some(item => item.status === "active"),
              })}
              onClick={selectedAlerts.some(item => item.status === "frozen") ? onUnfreezeClick : onFreezeClick}
            >
              {selectedAlerts.some(item => item.status === "frozen") ? "Unfreeze" : "Freeze"}
            </span>
          </div>
        </nav>
        <TableWrapper
          headerCells={vendorAlertsTableHeaderCells}
          data={convertAdminAlertsToTableRows(data?.data)}
          isLoading={isFetching}
          onCheckboxChange={onSelectAlert}
          currentItemIDs={selectedAlerts.map(item => item.id)}
          onHeaderCheckboxChange={toggleAllAlerts}
          isHeaderChecked={isHeaderChecked}
          additionalTags={
            selectedAlerts?.length
              ? [
                  {
                    label: "Selected Alerts",
                    values: [selectedAlerts.length],
                  },
                ]
              : []
          }
          clearAdditionalTags={() => setSelectedAlerts([])}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showAcknowledgeModal && (
        <AlertsAcknowledgeModal
          isVendor
          onCancel={() => setShowAcknowledgeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showFreezeModal && (
        <AlertsFreezeModal
          isVendor
          onCancel={() => setShowFreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
      {showUnfreezeModal && (
        <AlertsUnfreezeModal
          isVendor
          onCancel={() => setShowUnfreezeModal(false)}
          onSuccess={handleSuccess}
          currentAlerts={selectedAlerts}
        />
      )}
    </>
  )
}
